import { useLang } from "@/i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, Sparkles, ArrowUpRight, X } from "lucide-react";
import { motion } from "framer-motion";

export default function ConversionModal({ open, onClose }) {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="overflow-hidden rounded-[28px] border-zinc-200 p-0 sm:max-w-[560px]"
        data-testid="conversion-modal"
      >
        <DialogTitle className="sr-only">{t.conversion.title}</DialogTitle>
        <DialogDescription className="sr-only">{t.conversion.subtitle}</DialogDescription>

        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 bg-white/80 text-zinc-500 backdrop-blur transition hover:text-zinc-900"
          data-testid="conversion-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative overflow-hidden bg-zinc-950 px-7 pt-12 pb-7 text-white">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                "radial-gradient(500px 240px at 80% -10%, rgba(196,192,245,0.35), transparent), radial-gradient(400px 200px at 0% 110%, rgba(81,75,186,0.55), transparent)",
            }}
          />
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="relative inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--sentinel-accent)] backdrop-blur"
          >
            <Sparkles className="h-3 w-3" /> Cyrqle
          </motion.div>
          <h3 className="relative mt-4 text-3xl text-white md:text-4xl">
            {t.conversion.title}
          </h3>
          <p className="relative mt-3 max-w-md text-sm leading-relaxed text-zinc-300">
            {t.conversion.subtitle}
          </p>
        </div>

        <div className="px-7 py-7">
          <ul className="space-y-3">
            {t.conversion.bullets.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="flex items-start gap-3 text-sm text-zinc-700"
                data-testid={`conversion-bullet-${i}`}
              >
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--sentinel-tint)] text-[color:var(--sentinel)]">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {b}
              </motion.li>
            ))}
          </ul>

          <div className="mt-7 flex flex-col gap-2 sm:flex-row">
            <a
              href="https://app.sentinelzone.com/signup?ref=landing-upload"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex-1 justify-center"
              data-testid="conversion-primary-cta"
            >
              {t.conversion.primary}
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              onClick={onClose}
              className="btn-ghost justify-center"
              data-testid="conversion-secondary-cta"
            >
              {t.conversion.secondary}
            </button>
          </div>
          <p className="mt-3 text-center text-[11px] text-zinc-400">{t.conversion.saved}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
